import { useEffect, useState } from 'react'
import { EmergencyPowerSettingsModel } from '../models'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { isUndefined } from 'lodash'
import { getEmergencyPowerSettingsByStorage, updateSettings as updateEmergencyPowertSettings } from 'src/Libs/emergency-power/api/emergency-power.api'
import { EmergencyPowerSettingsUpdateDto } from 'src/Libs/emergency-power/api/dto'
import { SerialNoInvalidError } from '../../errors'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import usePrevious from '@e3dc-react/shell/Hooks/usePrevious'
import { useFarmContext } from 'src/Elements/Farm'

interface UseEmergencySettingsHook {
  /**
   * Updates the Emergency Power settings for a single storage device.
   * @param storageSerialNr
   * @param model
   */
  updateSettings: (storageSerialNo: string, model: EmergencyPowerSettingsUpdateDto) => Promise<void>

  error?: AuthFetchError | Error
  isLoading: boolean
  emergencyPowerSettings: EmergencyPowerSettingsModel[]
}

/**
 * The hook provides tools to load and update the emergency power settings.
 *
 * No data is cached anywhere.
 *
 *
 */
const useEmergencyPower = (storageSerialNo?: string): UseEmergencySettingsHook => {
  const authFetch = useAuthFetchWithResponse()
  const [emergencyPowerSettings, setEmergencyPowerSettings] = useState<EmergencyPowerSettingsModel[]>([])
  const [error, setError] = useState<AuthFetchError | Error>()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const { isFarmMasterSelected, farmMaster, farmClients } = useFarmContext()

  const prevStorageSerialNo = usePrevious(storageSerialNo)

  useEffect(() => {
    setError(undefined)

    if (storageSerialNo !== prevStorageSerialNo) {
      loadEmergencyPowerSettings()
    }
  }, [storageSerialNo]) // eslint-disable-line

  const loadEmergencyPowerSettings = async (): Promise<void> => {
    if (isUndefined(storageSerialNo)) {
      throw new SerialNoInvalidError(storageSerialNo)
    }

    setIsLoading(true)

    const storageList: string[] = isFarmMasterSelected && farmClients ? farmClients?.map(child => child.storageId) : [storageSerialNo]

    const promises = (storageList || []).map((item: string) => getEmergencyPowerSettingsByStorage(item, authFetch))
    const results = await Promise.all(promises)
    const emergencyPowerSettingsFiltered = results.reduce<EmergencyPowerSettingsModel[]>(
      (emergencyPowerSettingsArr, emergencyPowerSettingsResult, index) => {
        if (emergencyPowerSettingsResult.hasErrors || !emergencyPowerSettingsResult.value) {
          return emergencyPowerSettingsArr
        }

        return [
          ...emergencyPowerSettingsArr,
          {
            ...emergencyPowerSettingsResult.value,
            storageSerialNo: storageList[index],
            serverStorageSerialNo: farmMaster?.storageId,
          },
        ]
      },
      []
    )

    setEmergencyPowerSettings(emergencyPowerSettingsFiltered)

    setIsLoading(false)
  }

  const updateSettings = (storageSerialNo: string, model: EmergencyPowerSettingsUpdateDto): Promise<void> => {
    if (isUndefined(storageSerialNo)) {
      throw new Error(`Invalid activeStorageId or tabIndex (id: ${storageSerialNo})`)
    }

    return updateEmergencyPowertSettings(storageSerialNo, model, authFetch)
  }

  return { updateSettings, error, isLoading, emergencyPowerSettings }
}

export default useEmergencyPower
